import { useEffect } from 'react';
import { APP_VERSION } from '@/config';

const RELOADED_KEY = 'bestigium_com_reloaded_for';

/**
 * Guard de la autoactualización: recargar COMO MUCHO UNA VEZ por versión
 * detectada. Si tras recargar el bundle sigue siendo viejo (p. ej. version.json
 * actualizado pero el contenedor sin reconstruir), sin esto se entra en un
 * bucle de recargas infinito.
 */
export function shouldReloadForVersion(
  current: string | undefined,
  deployed: string | undefined,
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> = sessionStorage
): boolean {
  if (!current || !deployed) return false;
  if (deployed === current) {
    storage.removeItem(RELOADED_KEY); // al día: rearma para el próximo deploy
    return false;
  }
  if (storage.getItem(RELOADED_KEY) === deployed) return false; // ya recargado
  storage.setItem(RELOADED_KEY, deployed);
  return true;
}

// Autoactualización para sitio estático (regla 3 vitta_developer):
// consulta /version.json al montar y al volver a primer plano; si difiere de la
// versión compilada, recarga para coger el último despliegue (útil en "Añadir a
// pantalla de inicio" del iPhone).
export function useAutoUpdate() {
  useEffect(() => {
    let stop = false;

    const check = async () => {
      try {
        const res = await fetch('/version.json', { cache: 'no-store' });
        if (!res.ok) return;
        const { version } = (await res.json()) as { version?: string };
        if (!stop && shouldReloadForVersion(APP_VERSION, version)) {
          stop = true;
          window.location.reload();
        }
      } catch {
        /* sin conexión: reintenta al próximo foco */
      }
    };

    check();
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stop = true;
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);
}
